import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import axiosInstance from "../../api/axiosInstance";

const PAGE_SIZE = 10;
const FORM_DRAFT_KEY = "subcategory_form_draft";
const TOAST_DURATION = 4000;

const api = {
  list: (categoryId = "", search = "", active = "") => {
    const params = new URLSearchParams();
    if (categoryId) params.append("category_id", categoryId);
    if (search)     params.append("q",           search);
    if (active !== "") params.append("is_active", active);
    return axiosInstance.get(`/api/superadmin/master/subcategories/?${params}`);
  },
  create: (body) =>
    axiosInstance.post(`/api/superadmin/master/subcategories/`, body),
  get: (id) =>
    axiosInstance.get(`/api/superadmin/master/subcategories/${id}/`),
  update: (id, body) =>
    axiosInstance.patch(`/api/superadmin/master/subcategories/${id}/`, body),
  remove: (id) =>
    axiosInstance.delete(`/api/superadmin/master/subcategories/${id}/`),
  searchCategories: (search = "", isActive = "") => {
    const params = new URLSearchParams();
    if (search)        params.append("q",         search);
    if (isActive !== "") params.append("is_active", isActive);
    return axiosInstance.get(`/api/superadmin/master/categories/?${params}`)
      .then((r) => (r.data?.results || []))
      .catch(() => []);
  },
};

const formDraft = {
  save:  (data) => { try { sessionStorage.setItem(FORM_DRAFT_KEY, JSON.stringify(data)); } catch { /**/ } },
  load:  ()     => { try { const r = sessionStorage.getItem(FORM_DRAFT_KEY); return r ? JSON.parse(r) : null; } catch { return null; } },
  clear: ()     => { try { sessionStorage.removeItem(FORM_DRAFT_KEY); } catch { /**/ } },
};


const mapApiErrors = (json) => {
  if (!json || typeof json !== "object") return { fieldErrors: {}, globalMessage: null };
  if (json.detail && typeof json.detail === "string") {
    return { fieldErrors: {}, globalMessage: json.detail };
  }
  const fieldErrors = {};
  let globalMessage = null;
  Object.entries(json).forEach(([key, val]) => {
    const msg = Array.isArray(val) ? val[0] : String(val);
    if (key === "non_field_errors") {
      globalMessage = msg;
    } else if (key !== "status") {
      fieldErrors[key] = msg;
    }
  });
  return { fieldErrors, globalMessage };
};

let _toastSetter = null;
const toast = {
  _push: (item) => {
    const id = Date.now() + Math.random();
    _toastSetter?.((p) => [...p, { id, ...item }]);
    return id;
  },
  success: (title, msg)  => toast._push({ type: "success", title, msg }),
  error:   (title, msg)  => toast._push({ type: "error",   title, msg }),
  info:    (title, msg)  => toast._push({ type: "info",    title, msg }),
  loading: (title)       => toast._push({ type: "loading", title, persistent: true }),
  dismiss: (id)          => _toastSetter?.((p) => p.filter((t) => t.id !== id)),
};

const TOAST_META = {
  success: { bar: "#16a34a", icon: "✓" },
  error:   { bar: "#dc2626", icon: "✕" },
  info:    { bar: "#1d4ed8", icon: "i" },
  loading: { bar: "#94a3b8", icon: null },
};

const ToastItem = ({ t, onDismiss }) => {
  const meta = TOAST_META[t.type];
  const [progress, setProgress] = useState(100);
  const [exiting,  setExiting]  = useState(false);
  const rafRef  = useRef(null);
  const startTs = useRef(Date.now());

  const dismiss = useCallback(() => {
    setExiting(true);
    setTimeout(() => onDismiss(t.id), 280);
  }, [t.id, onDismiss]);

  useEffect(() => {
    if (t.persistent) return;
    const tick = () => {
      const pct = Math.max(0, 100 - ((Date.now() - startTs.current) / TOAST_DURATION) * 100);
      setProgress(pct);
      if (pct > 0) rafRef.current = requestAnimationFrame(tick);
      else dismiss();
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafRef.current);
  }, [t.persistent, dismiss]);

  const CloseX = () => (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2">
      <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
    </svg>
  );

  return (
    <div style={{
      position: "relative", overflow: "hidden", background: "#fff",
      borderRadius: 10, borderLeft: `4px solid ${meta.bar}`,
      boxShadow: "0 4px 20px rgba(0,0,0,0.10), 0 0 0 1px rgba(0,0,0,0.06)",
      minWidth: 300, maxWidth: 380,
      animation: exiting
        ? "smToastOut 0.28s ease forwards"
        : "smToastIn 0.28s cubic-bezier(0.34,1.56,0.64,1) forwards",
    }}>
      <div style={{ display: "flex", alignItems: "flex-start", gap: 10, padding: "13px 14px 14px" }}>
        <div style={{
          flexShrink: 0, width: 24, height: 24, borderRadius: "50%",
          background: `${meta.bar}18`,
          display: "flex", alignItems: "center", justifyContent: "center", marginTop: 1,
        }}>
          {t.type === "loading" ? (
            <svg width="13" height="13" viewBox="0 0 13 13" fill="none" style={{ animation: "smSpin 0.85s linear infinite" }}>
              <circle cx="6.5" cy="6.5" r="5" stroke={meta.bar} strokeWidth="2" strokeDasharray="8 20" strokeLinecap="round" />
            </svg>
          ) : (
            <span style={{ fontSize: 11, fontWeight: 800, color: meta.bar, lineHeight: 1 }}>{meta.icon}</span>
          )}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <p style={{ margin: 0, fontSize: 13.5, fontWeight: 650, color: "#0f172a", lineHeight: 1.35 }}>{t.title}</p>
          {t.msg && <p style={{ margin: "3px 0 0", fontSize: 12.5, color: "#64748b", lineHeight: 1.5 }}>{t.msg}</p>}
        </div>
        <button onClick={dismiss}
          style={{ background: "none", border: "none", cursor: "pointer", color: "#cbd5e1", padding: "2px", flexShrink: 0, display: "flex", borderRadius: 4, transition: "color 0.15s", marginTop: 1 }}
          onMouseEnter={(e) => e.currentTarget.style.color = "#94a3b8"}
          onMouseLeave={(e) => e.currentTarget.style.color = "#cbd5e1"}>
          <CloseX />
        </button>
      </div>
      {!t.persistent && (
        <div style={{ position: "absolute", bottom: 0, left: 0, right: 0, height: 3, background: "#f1f5f9" }}>
          <div style={{ height: "100%", width: `${progress}%`, background: meta.bar, transition: "width 0.1s linear", borderRadius: "0 2px 0 0" }} />
        </div>
      )}
    </div>
  );
};

const ToastContainer = () => {
  const [toasts, setToasts] = useState([]);
  _toastSetter = setToasts;
  const dismiss = useCallback((id) => setToasts((p) => p.filter((t) => t.id !== id)), []);
  if (!toasts.length) return null;
  return (
    <div style={{ position: "fixed", bottom: 24, right: 24, zIndex: 9999, display: "flex", flexDirection: "column", gap: 10, alignItems: "flex-end" }}>
      {toasts.map((t) => <ToastItem key={t.id} t={t} onDismiss={dismiss} />)}
    </div>
  );
};

const RestoreIcon = () => (
  <svg width="13" height="13" viewBox="0 0 13 13" fill="none">
    <path d="M11 6.5A4.5 4.5 0 112 4.5M2 2v2.5h2.5" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const DraftBanner = ({ onRestore, onDiscard }) => (
  <div style={{
    background: "#fffbeb", border: "1px solid #fde68a", borderRadius: 8,
    padding: "10px 14px", marginBottom: 16,
    display: "flex", alignItems: "center", justifyContent: "space-between",
    gap: 10, flexWrap: "wrap",
  }}>
    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
      <span style={{ fontSize: 16 }}>📝</span>
      <div>
        <p style={{ margin: 0, fontSize: 13, fontWeight: 600, color: "#92400e" }}>Unsaved draft found</p>
        <p style={{ margin: 0, fontSize: 12, color: "#b45309" }}>You have a previously unsaved form. Would you like to restore it?</p>
      </div>
    </div>
    <div style={{ display: "flex", gap: 8, flexShrink: 0 }}>
      <button onClick={onDiscard}
        style={{ padding: "5px 12px", borderRadius: 8, fontSize: 12.5, fontWeight: 570, cursor: "pointer", background: "#f1f5f9", color: "#475569", border: "1.5px solid #e2e8f0" }}>
        Discard
      </button>
      <button onClick={onRestore}
        style={{ padding: "5px 12px", borderRadius: 8, fontSize: 12.5, fontWeight: 570, cursor: "pointer", background: "#d97706", color: "#fff", border: "none", display: "inline-flex", alignItems: "center", gap: 6 }}
        onMouseEnter={(e) => e.currentTarget.style.background = "#b45309"}
        onMouseLeave={(e) => e.currentTarget.style.background = "#d97706"}>
        <RestoreIcon /> Restore
      </button>
    </div>
  </div>
);

const PlusIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
    <line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" />
  </svg>
);
const EditIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
  </svg>
);
const SearchIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
  </svg>
);
const CloseIcon = () => (
  <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2">
    <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);
const SmallXIcon = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.8">
    <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);
const ChevronLeftIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2">
    <polyline points="15 18 9 12 15 6" />
  </svg>
);
const ChevronRightIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2">
    <polyline points="9 18 15 12 9 6" />
  </svg>
);
const ChevronDownIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2">
    <polyline points="6 9 12 15 18 9" />
  </svg>
);

// ── NEW: Warning and CheckCircle icons for confirm dialogs ────────────────────
const WarningIcon = () => (
  <svg width="48" height="48" viewBox="0 0 48 48" fill="none">
    <circle cx="24" cy="24" r="24" fill="#FEF3C7" />
    <path d="M24 13L11 37h26L24 13z" stroke="#D97706" strokeWidth="2" strokeLinejoin="round" fill="#FDE68A" />
    <path d="M24 22v7M24 31.5v1" stroke="#D97706" strokeWidth="2.2" strokeLinecap="round" />
  </svg>
);
const CheckCircleIcon = () => (
  <svg width="48" height="48" viewBox="0 0 48 48" fill="none">
    <circle cx="24" cy="24" r="24" fill="#DCFCE7" />
    <circle cx="24" cy="24" r="14" stroke="#16a34a" strokeWidth="2" fill="#BBF7D0" />
    <path d="M17 24l5 5 9-9" stroke="#16a34a" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const SkeletonRow = () => (
  <tr style={{ borderBottom: "1px solid #f1f5f9" }}>
    {[32, 170, 110, 150, 70, 120, 90].map((w, i) => (
      <td key={i} style={{ padding: "14px 16px" }}>
        <div style={{
          height: "13px", width: `${w}px`, maxWidth: "100%", borderRadius: "6px",
          background: "linear-gradient(90deg,#f1f5f9 25%,#e2e8f0 50%,#f1f5f9 75%)",
          backgroundSize: "200% 100%", animation: "smShimmer 1.4s infinite",
        }} />
      </td>
    ))}
  </tr>
);
const TableSkeleton = () => <>{Array.from({ length: PAGE_SIZE }).map((_, i) => <SkeletonRow key={i} />)}</>;

const Pagination = ({ currentPage, totalPages, totalCount, onPageChange }) => {
  if (totalPages <= 1) return null;
  const startItem = (currentPage - 1) * PAGE_SIZE + 1;
  const endItem   = Math.min(currentPage * PAGE_SIZE, totalCount);

  const getPages = () => {
    const pages = [];
    if (totalPages <= 7) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
    } else {
      pages.push(1);
      if (currentPage > 3) pages.push("...");
      for (let i = Math.max(2, currentPage - 1); i <= Math.min(totalPages - 1, currentPage + 1); i++) pages.push(i);
      if (currentPage < totalPages - 2) pages.push("...");
      pages.push(totalPages);
    }
    return pages;
  };

  const btnBase = {
    display: "inline-flex", alignItems: "center", justifyContent: "center",
    minWidth: "34px", height: "34px", padding: "0 6px",
    border: "1px solid #e2e8f0", borderRadius: "8px",
    fontSize: "13px", fontWeight: 500, cursor: "pointer",
    background: "#fff", color: "#374151", transition: "all 0.15s", lineHeight: 1,
  };

  return (
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "14px 20px", borderTop: "1px solid #f1f5f9", background: "#f8fafc", flexWrap: "wrap", gap: "10px" }}>
      <span style={{ fontSize: "13px", color: "#64748b" }}>
        Showing <strong style={{ color: "#374151" }}>{startItem}–{endItem}</strong> of{" "}
        <strong style={{ color: "#374151" }}>{totalCount}</strong> results
      </span>
      <div style={{ display: "flex", gap: "4px", alignItems: "center" }}>
        <button onClick={() => onPageChange(currentPage - 1)} disabled={currentPage === 1}
          style={{ ...btnBase, opacity: currentPage === 1 ? 0.4 : 1, cursor: currentPage === 1 ? "not-allowed" : "pointer" }}>
          <ChevronLeftIcon />
        </button>
        {getPages().map((p, i) =>
          p === "..." ? (
            <span key={`e-${i}`} style={{ padding: "0 4px", color: "#94a3b8", fontSize: "13px", lineHeight: "34px" }}>…</span>
          ) : (
            <button key={p} onClick={() => onPageChange(p)} className={p === currentPage ? "" : "sm-page-btn"}
              style={{ ...btnBase, ...(p === currentPage ? { background: "#3b82f6", color: "#fff", border: "1px solid #3b82f6", fontWeight: 600 } : {}) }}>
              {p}
            </button>
          )
        )}
        <button onClick={() => onPageChange(currentPage + 1)} disabled={currentPage === totalPages}
          style={{ ...btnBase, opacity: currentPage === totalPages ? 0.4 : 1, cursor: currentPage === totalPages ? "not-allowed" : "pointer" }}>
          <ChevronRightIcon />
        </button>
      </div>
    </div>
  );
};

const Toggle = ({ value, onChange }) => (
  <div onClick={() => onChange(!value)} style={{ display: "flex", alignItems: "center", gap: "8px", cursor: "pointer", userSelect: "none" }}>
    <div style={{ width: "42px", height: "24px", borderRadius: "12px", background: value ? "#22c55e" : "#cbd5e1", position: "relative", transition: "background 0.2s" }}>
      <div style={{ position: "absolute", top: "4px", left: value ? "22px" : "4px", width: "16px", height: "16px", borderRadius: "50%", background: "#fff", transition: "left 0.2s", boxShadow: "0 1px 3px rgba(0,0,0,0.2)" }} />
    </div>
    <span style={{ fontSize: "13px", fontWeight: 600, color: value ? "#16a34a" : "#94a3b8" }}>
      {value ? "Active" : "Inactive"}
    </span>
  </div>
);

const Badge = ({ active }) => (
  <span style={{
    display: "inline-flex", alignItems: "center", gap: "5px",
    padding: "3px 10px", borderRadius: "20px", fontSize: "12px", fontWeight: 600,
    ...(active ? { background: "#dcfce7", color: "#16a34a" } : { background: "#fee2e2", color: "#dc2626" }),
  }}>
    <span style={{ width: "6px", height: "6px", borderRadius: "50%", background: active ? "#22c55e" : "#f43f5e", display: "inline-block" }} />
    {active ? "Active" : "Inactive"}
  </span>
);

const Modal = ({ title, onClose, children }) => {
  useEffect(() => {
    document.body.style.overflow = "hidden";
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = "";
      document.removeEventListener("keydown", onKey);
    };
  }, [onClose]);

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(15,23,42,0.5)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000, padding: "20px" }}
      onClick={onClose}>
      <div style={{ background: "#fff", borderRadius: "14px", width: "100%", maxWidth: "520px", boxShadow: "0 24px 64px rgba(0,0,0,0.18)", animation: "smFadeUp 0.2s ease", maxHeight: "90vh", overflowY: "auto" }}
        onClick={(e) => e.stopPropagation()}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "16px 24px", borderBottom: "1px solid #f1f5f9", position: "sticky", top: 0, background: "#fff", zIndex: 1 }}>
          <h2 style={{ margin: 0, fontSize: "17px", fontWeight: 700, color: "#0f172a" }}>{title}</h2>
        </div>
        <div style={{ padding: "20px 24px 24px" }}>{children}</div>
      </div>
    </div>
  );
};

const CategorySearchDropdown = ({
  value,
  onChange,
  hasError = false,
  placeholder = "Select category…",
  dropdownZIndex = 700,
}) => {
  const [open,      setOpen]      = useState(false);
  const [query,     setQuery]     = useState("");
  const [results,   setResults]   = useState([]);
  const [searching, setSearching] = useState(false);
  const wrapRef     = useRef(null);
  const inputRef    = useRef(null);
  const debounceRef = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) {
        setOpen(false);
        setQuery("");
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  useEffect(() => {
    if (!open) return;
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      setSearching(true);
      try {
        const { data } = await api.searchCategories(query, "");
        setResults(data?.results || []);
      } catch {
        setResults([]);
      } finally {
        setSearching(false);
      }
    }, 300);
    return () => clearTimeout(debounceRef.current);
  }, [query, open]);

  const handleOpen = () => {
    setOpen(true);
    setQuery("");
    setTimeout(() => inputRef.current?.focus(), 60);
  };

  const handleSelect = (cat) => {
    onChange({ id: cat.id, name: cat.name });
    setOpen(false);
    setQuery("");
  };

  const handleClear = (e) => {
    e.stopPropagation();
    onChange(null);
    setOpen(false);
  };

  const borderColor = hasError ? "#f43f5e" : open ? "#3b82f6" : "#e2e8f0";
  const bgColor     = hasError ? "#fff1f2" : value ? "#f0f9ff" : "#fff";
  const boxShadow   = open
    ? "0 0 0 3px rgba(59,130,246,0.1)"
    : hasError ? "0 0 0 3px rgba(244,63,94,0.1)" : "none";

  return (
    <div ref={wrapRef} style={{ position: "relative", width: "100%" }}>
      <div
        onClick={handleOpen}
        style={{
          display: "flex", alignItems: "center", justifyContent: "space-between",
          padding: "0 10px 0 12px", height: "38px",
          border: `1px solid ${borderColor}`,
          borderRadius: open ? "8px 8px 0 0" : "8px",
          fontSize: "14px",
          color: value ? "#1e293b" : "#94a3b8",
          background: bgColor,
          cursor: "pointer", userSelect: "none",
          boxShadow,
          transition: "border-color 0.15s, background 0.15s, box-shadow 0.15s",
          boxSizing: "border-box",
        }}
      >
        <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", flex: 1, fontSize: "14px" }}>
          {value ? value.name : placeholder}
        </span>
        <span style={{ display: "flex", alignItems: "center", gap: "2px", marginLeft: "6px", flexShrink: 0 }}>
          {value && (
            <span
              onClick={handleClear}
              title="Clear"
              style={{ display: "flex", alignItems: "center", justifyContent: "center", width: "18px", height: "18px", color: "#94a3b8", cursor: "pointer", borderRadius: "4px" }}
            >
              <SmallXIcon />
            </span>
          )}
          <span style={{ color: "#94a3b8", display: "flex", transform: open ? "rotate(180deg)" : "rotate(0deg)", transition: "transform 0.2s" }}>
            <ChevronDownIcon />
          </span>
        </span>
      </div>

      {open && (
        <div style={{
          position: "absolute", top: "100%", left: 0, right: 0, zIndex: dropdownZIndex,
          background: "#fff",
          border: "1px solid #3b82f6", borderTop: "none",
          borderRadius: "0 0 10px 10px",
          boxShadow: "0 8px 28px rgba(0,0,0,0.13)",
          animation: "smFadeUp 0.15s ease",
        }}>
          <div style={{ padding: "7px 8px 6px", borderBottom: "1px solid #f1f5f9", position: "relative" }}>
            <span style={{ position: "absolute", left: "16px", top: "50%", transform: "translateY(-50%)", color: "#94a3b8", display: "flex", pointerEvents: "none" }}>
              <SearchIcon />
            </span>
            <input
              ref={inputRef}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search categories…"
              style={{
                width: "100%", padding: "6px 8px 6px 30px",
                border: "1px solid #e2e8f0", borderRadius: "6px",
                fontSize: "13px", color: "#1e293b", outline: "none",
                background: "#f8fafc", boxSizing: "border-box",
              }}
            />
          </div>
          <div style={{ maxHeight: "220px", overflowY: "auto" }}>
            {searching ? (
              <div style={{ padding: "16px", textAlign: "center" }}>
                <div style={{ display: "inline-flex", gap: "6px", alignItems: "center", color: "#94a3b8", fontSize: "13px" }}>
                  <div style={{ width: "13px", height: "13px", border: "2px solid #e2e8f0", borderTopColor: "#3b82f6", borderRadius: "50%", animation: "smSpin 0.7s linear infinite" }} />
                  Searching…
                </div>
              </div>
            ) : results.length === 0 ? (
              <div style={{ padding: "18px", textAlign: "center", color: "#94a3b8", fontSize: "13px" }}>
                {query ? `No results for "${query}"` : "No categories found"}
              </div>
            ) : (
              results.map((cat) => {
                const isSelected = value?.id === cat.id;
                return (
                  <div
                    key={cat.id}
                    onClick={() => handleSelect(cat)}
                    onMouseEnter={(e) => { if (!isSelected) e.currentTarget.style.background = "#f8fafc"; }}
                    onMouseLeave={(e) => { if (!isSelected) e.currentTarget.style.background = "transparent"; }}
                    style={{
                      display: "flex", alignItems: "center", justifyContent: "space-between",
                      padding: "9px 14px", cursor: "pointer",
                      background: isSelected ? "#eff6ff" : "transparent",
                      borderLeft: isSelected ? "3px solid #3b82f6" : "3px solid transparent",
                      transition: "background 0.1s",
                    }}
                  >
                    <span style={{
                      fontSize: "13px", fontWeight: isSelected ? 600 : 400,
                      color: isSelected ? "#1e40af" : "#0f172a",
                      overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                    }}>
                      {cat.name}
                    </span>
                    <span style={{
                      fontSize: "11px", fontWeight: 600, flexShrink: 0, marginLeft: "8px",
                      color: cat.is_active ? "#16a34a" : "#dc2626",
                      background: cat.is_active ? "#dcfce7" : "#fee2e2",
                      padding: "1px 7px", borderRadius: "10px",
                    }}>
                      {cat.is_active ? "Active" : "Inactive"}
                    </span>
                  </div>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const CategoryFilterDropdown = ({ value, onChange }) => (
  <div style={{ width: "210px", flexShrink: 0 }}>
    <CategorySearchDropdown value={value} onChange={onChange} placeholder="All Categories" dropdownZIndex={700} />
  </div>
);

// ── CHANGE 1: Stats bar (same pattern as StateMaster) ─────────────────────────
const StatsBar = ({ allData, loading }) => {
  const total    = allData.length;
  const active   = allData.filter((s) => s.is_active).length;
  const inactive = total - active;

  const Card = ({ label, count, color, bg, border }) => (
    <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 18px", borderRadius: 10, background: bg, border: `1px solid ${border}`, minWidth: 110 }}>
      <div style={{ width: 8, height: 8, borderRadius: "50%", background: color, flexShrink: 0 }} />
      <div>
        <div style={{ fontSize: 18, fontWeight: 700, color: "#0f172a", lineHeight: 1.1 }}>
          {loading
            ? <div style={{ width: 28, height: 16, borderRadius: 4, background: "#e2e8f0", animation: "smShimmer 1.4s infinite" }} />
            : count}
        </div>
        <div style={{ fontSize: 11.5, color: "#64748b", fontWeight: 550, marginTop: 2, letterSpacing: "0.02em" }}>{label}</div>
      </div>
    </div>
  );

  return (
    <div style={{ display: "flex", gap: 10, marginBottom: 14, flexWrap: "wrap" }}>
      <Card label="Total Subcategories" count={total}    color="#6366f1" bg="#f5f3ff" border="#e0e7ff" />
      <Card label="Active"              count={active}   color="#16a34a" bg="#f0fdf4" border="#bbf7d0" />
      <Card label="Inactive"            count={inactive} color="#dc2626" bg="#fef2f2" border="#fecaca" />
    </div>
  );
};

// ── Deactivate confirm (unchanged) ────────────────────────────────────────────
const DeactivateConfirm = ({ item, onConfirm, onCancel, loading }) => (
  <div style={{ textAlign: "center", padding: "4px 0 0" }}>
    <div style={{ display: "flex", justifyContent: "center", marginBottom: 16 }}><WarningIcon /></div>
    <p style={{ fontSize: "16px", fontWeight: 600, color: "#0f172a", margin: "0 0 6px" }}>
      Deactivate <span style={{ color: "#f43f5e" }}>"{item.name}"</span>?
    </p>
    <p style={{ fontSize: "13px", color: "#94a3b8", margin: "0 0 24px" }}>
      This will mark the subcategory as inactive. You can reactivate it later.
    </p>
    <div style={{ display: "flex", justifyContent: "center", gap: "10px" }}>
      <button onClick={onCancel} disabled={loading}
        style={{ padding: "9px 20px", border: "1px solid #e2e8f0", borderRadius: "8px", background: "#fff", color: "#64748b", fontSize: "14px", fontWeight: 600, cursor: "pointer", opacity: loading ? 0.6 : 1 }}>
        Cancel
      </button>
      <button onClick={onConfirm} disabled={loading}
        style={{ padding: "9px 22px", border: "none", borderRadius: "8px", background: "#f43f5e", color: "#fff", fontSize: "14px", fontWeight: 600, cursor: loading ? "not-allowed" : "pointer", opacity: loading ? 0.7 : 1 }}>
        {loading ? "Deactivating…" : "Yes, Deactivate"}
      </button>
    </div>
  </div>
);

// ── CHANGE 2: Activate confirm (mirrors DeactivateConfirm, same as StateMaster) ─
const ActivateConfirm = ({ item, onConfirm, onCancel, loading }) => (
  <div style={{ textAlign: "center", padding: "4px 0 0" }}>
    <div style={{ display: "flex", justifyContent: "center", marginBottom: 16 }}><CheckCircleIcon /></div>
    <p style={{ fontSize: "16px", fontWeight: 600, color: "#0f172a", margin: "0 0 6px" }}>
      Activate <span style={{ color: "#16a34a" }}>"{item.name}"</span>?
    </p>
    <p style={{ fontSize: "13px", color: "#94a3b8", margin: "0 0 24px" }}>
      This will mark the subcategory as active and make it available for use.
    </p>
    <div style={{ display: "flex", justifyContent: "center", gap: "10px" }}>
      <button onClick={onCancel} disabled={loading}
        style={{ padding: "9px 20px", border: "1px solid #e2e8f0", borderRadius: "8px", background: "#fff", color: "#64748b", fontSize: "14px", fontWeight: 600, cursor: "pointer", opacity: loading ? 0.6 : 1 }}>
        Cancel
      </button>
      <button onClick={onConfirm} disabled={loading}
        style={{ padding: "9px 22px", border: "none", borderRadius: "8px", background: "#16a34a", color: "#fff", fontSize: "14px", fontWeight: 600, cursor: loading ? "not-allowed" : "pointer", opacity: loading ? 0.7 : 1 }}>
        {loading ? "Activating…" : "Yes, Activate"}
      </button>
    </div>
  </div>
);

// ─── Subcategory Form ─────────────────────────────────────────────────────────
const EMPTY_FORM = { name: "", description: "", sort_order: "", is_active: true };

const SubcategoryForm = ({ initial, onSubmit, onCancel, loading }) => {
  const isEdit     = !!initial?.id;
  const savedDraft = !isEdit ? formDraft.load() : null;

  const seedCategoryFromInitial =
    initial?.id && (initial.category || initial.category_id)
      ? { id: initial.category ?? initial.category_id, name: initial.category_name ?? "" }
      : null;

  const [showBanner, setShowBanner] = useState(!isEdit && !!savedDraft);
  const [category, setCategory]     = useState(isEdit ? seedCategoryFromInitial : null);
  const [form, setForm]             = useState(isEdit ? {
    name:        initial?.name        ?? "",
    description: initial?.description ?? "",
    sort_order:  initial?.sort_order  ?? "",
    is_active:   initial?.is_active   ?? true,
  } : EMPTY_FORM);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (isEdit || showBanner) return;
    formDraft.save({ ...form, category });
  }, [form, category, isEdit, showBanner]);

  const set = (k, v) => {
    setForm((f) => ({ ...f, [k]: v }));
    if (errors[k]) setErrors((e) => ({ ...e, [k]: "" }));
  };

  const handleRestore = () => {
    if (savedDraft) {
      const { category: draftCat, ...draftForm } = savedDraft;
      setForm((f) => ({ ...f, ...draftForm }));
      setCategory(draftCat || null);
    }
    setShowBanner(false);
  };

  const handleDiscard = () => {
    formDraft.clear();
    setShowBanner(false);
    setCategory(null);
    setForm(EMPTY_FORM);
  };

  const handleCancel = () => {
    if (!isEdit) formDraft.clear();
    onCancel();
  };

  const validate = () => {
    const e = {};
    if (!category?.id)                                             e.category_id = "Please select a category.";
    if (!form.name.trim())                                         e.name        = "Subcategory name is required.";
    if (form.sort_order === "" || isNaN(Number(form.sort_order))) e.sort_order  = "A valid number is required.";
    setErrors(e);
    return !Object.keys(e).length;
  };

  const handleSubmit = () => {
    if (!validate()) return;
    onSubmit({
      category_id: Number(category.id),
      name:        form.name.trim(),
      description: form.description.trim(),
      sort_order:  Number(form.sort_order),
      is_active:   form.is_active,
    }, setErrors);
  };

  const inputStyle = (key) => ({
    width: "100%", padding: "9px 12px",
    border: `1px solid ${errors[key] ? "#f43f5e" : "#e2e8f0"}`,
    borderRadius: "8px", fontSize: "14px", color: "#1e293b", outline: "none",
    background: errors[key] ? "#fff1f2" : "#f8fafc", boxSizing: "border-box",
    fontFamily: "inherit", transition: "border 0.15s",
  });
  const labelStyle = { fontSize: "13px", fontWeight: 600, color: "#374151" };
  const errLabel   = { ...labelStyle, color: "#f43f5e" };

  return (
    <div>
      {showBanner && <DraftBanner onRestore={handleRestore} onDiscard={handleDiscard} />}

      {errors.__global && (
        <div style={{ background: "#fff5f5", border: "1px solid #fecaca", borderRadius: 8, padding: "10px 14px", marginBottom: 14 }}>
          <p style={{ margin: 0, fontSize: 13, color: "#dc2626", fontWeight: 550 }}>⚠ {errors.__global}</p>
        </div>
      )}

      <div style={{ display: "flex", flexDirection: "column", gap: "5px", marginBottom: "14px" }}>
        <label style={errors.category_id ? errLabel : labelStyle}>
          Category <span style={{ color: "#f43f5e" }}>*</span>
        </label>
        <CategorySearchDropdown
          value={category}
          onChange={(val) => {
            setCategory(val);
            if (errors.category_id) setErrors((e) => ({ ...e, category_id: "" }));
          }}
          hasError={!!errors.category_id}
          placeholder="Search and select a category…"
          dropdownZIndex={1100}
        />
        {errors.category_id && <span style={{ fontSize: "12px", color: "#f43f5e" }}>⚠ {errors.category_id}</span>}
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "14px", marginBottom: "14px" }}>
        <div style={{ display: "flex", flexDirection: "column", gap: "5px" }}>
          <label style={errors.name ? errLabel : labelStyle}>
            Subcategory Name <span style={{ color: "#f43f5e" }}>*</span>
          </label>
          <input
            style={inputStyle("name")} type="text" placeholder="e.g. Laptops & Desktops"
            value={form.name} onChange={(e) => set("name", e.target.value)}
            onFocus={(e) => { e.target.style.borderColor = "#3b82f6"; e.target.style.background = "#fff"; }}
            onBlur={(e)  => { e.target.style.borderColor = errors.name ? "#f43f5e" : "#e2e8f0"; e.target.style.background = errors.name ? "#fff1f2" : "#f8fafc"; }}
          />
          {errors.name && <span style={{ fontSize: "12px", color: "#f43f5e" }}>⚠ {errors.name}</span>}
        </div>

        <div style={{ display: "flex", flexDirection: "column", gap: "5px" }}>
          <label style={errors.sort_order ? errLabel : labelStyle}>
            Sort Order <span style={{ color: "#f43f5e" }}>*</span>
          </label>
          <input
            style={inputStyle("sort_order")} type="number" placeholder="e.g. 10"
            value={form.sort_order} onChange={(e) => set("sort_order", e.target.value)}
            onFocus={(e) => { e.target.style.borderColor = "#3b82f6"; e.target.style.background = "#fff"; }}
            onBlur={(e)  => { e.target.style.borderColor = errors.sort_order ? "#f43f5e" : "#e2e8f0"; e.target.style.background = errors.sort_order ? "#fff1f2" : "#f8fafc"; }}
          />
          {errors.sort_order && <span style={{ fontSize: "12px", color: "#f43f5e" }}>⚠ {errors.sort_order}</span>}
        </div>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr auto", gap: "14px", alignItems: "flex-start", marginBottom: "14px" }}>
        <div style={{ display: "flex", flexDirection: "column", gap: "5px" }}>
          <label style={labelStyle}>Description</label>
          <textarea
            style={{ ...inputStyle("description"), resize: "vertical", minHeight: "80px", padding: "9px 12px" }}
            placeholder="Optional description…"
            value={form.description}
            onChange={(e) => set("description", e.target.value)}
          />
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: "5px" }}>
          <label style={labelStyle}>Status</label>
          <div style={{ paddingTop: "10px" }}>
            <Toggle value={form.is_active} onChange={(v) => set("is_active", v)} />
          </div>
        </div>
      </div>

      <div style={{ display: "flex", justifyContent: "flex-end", gap: "10px", paddingTop: "8px", borderTop: "1px solid #f1f5f9" }}>
        <button onClick={handleCancel} disabled={loading}
          style={{ padding: "9px 20px", border: "1px solid #e2e8f0", borderRadius: "8px", background: "#fff", color: "#64748b", fontSize: "14px", fontWeight: 600, cursor: loading ? "not-allowed" : "pointer", opacity: loading ? 0.6 : 1 }}>
          Cancel
        </button>
        <button onClick={handleSubmit} disabled={loading}
          style={{ padding: "9px 22px", border: "none", borderRadius: "8px", background: "#3b82f6", color: "#fff", fontSize: "14px", fontWeight: 600, cursor: loading ? "not-allowed" : "pointer", opacity: loading ? 0.7 : 1 }}>
          {loading ? "Saving…" : isEdit ? "Update Subcategory" : "Add Subcategory"}
        </button>
      </div>
    </div>
  );
};

const SubcategoryMaster = () => {
  const [allData,       setAllData]       = useState([]);
  const [loading,       setLoading]       = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [deletingId,    setDeletingId]    = useState(null);
  const [activatingId,  setActivatingId]  = useState(null);
  const [currentPage,   setCurrentPage]   = useState(1);

  const [search,         setSearch]         = useState("");
  const [categoryFilter, setCategoryFilter] = useState(null);
  const [activeFilter,   setActiveFilter]   = useState("");

  const [showAdd,  setShowAdd]  = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [delItem,  setDelItem]  = useState(null);
  // ── CHANGE 2: new state for activate confirm dialog ──
  const [activateItem,  setActivateItem]  = useState(null);
  const [activateLoading, setActivateLoading] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.list(
        categoryFilter ? String(categoryFilter.id) : "",
        search,
        activeFilter
      );

      if (data?.status === 401) {
        toast.error("Session Expired", "Please log in again to continue.");
        setLoading(false);
        return;
      }
      if (data?.status === 403) {
        toast.error("Access Denied", "You don't have permission to view subcategories.");
        setLoading(false);
        return;
      }
      if (data?.status >= 400) {
        const msg = data?.data?.detail || `Server error (${data?.status}). Please try again.`;
        toast.error("Failed to Load", msg);
        setLoading(false);
        return;
      }

      const rows = data?.results ?? (Array.isArray(data) ? data : []);
      setAllData([...rows].sort((a, b) => (b.is_active ? 1 : 0) - (a.is_active ? 1 : 0)));
      setCurrentPage(1);
    } catch (err) {
      if (err?.name === "TypeError") {
        toast.error("Network Error", "Unable to reach the server. Check your connection.");
      } else {
        toast.error("Failed to Load", "Unable to fetch subcategories. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  }, [search, categoryFilter, activeFilter]);

  useEffect(() => {
    const t = setTimeout(fetchData, 350);
    return () => clearTimeout(t);
  }, [fetchData]);

  const totalCount = allData.length;
  const totalPages = Math.ceil(totalCount / PAGE_SIZE);
  const pagedData  = useMemo(() => {
    const start = (currentPage - 1) * PAGE_SIZE;
    return allData.slice(start, start + PAGE_SIZE);
  }, [allData, currentPage]);

  const handlePageChange = (page) => {
    if (page < 1 || page > totalPages) return;
    setCurrentPage(page);
  };

  const applyFieldErrors = (data, setErrors) => {
    const { fieldErrors, globalMessage } = mapApiErrors(data);
    const hasFields = Object.keys(fieldErrors).length > 0 || !!globalMessage;
    if (hasFields) {
      setErrors({ ...fieldErrors, ...(globalMessage ? { __global: globalMessage } : {}) });
      return true;
    }
    return false;
  };

  const handleCreate = async (body, setErrors) => {
    setActionLoading(true);
    const tid = toast.loading("Adding subcategory…");
    try {
      const { data } = await api.create(body);
      toast.dismiss(tid);

      if (data?.status >= 400) {
        if (data?.data && applyFieldErrors(data?.data, setErrors)) {
          toast.error("Validation Error", "Please fix the highlighted fields.");
          return;
        }
        const msg = data?.data?.detail || `Failed to create subcategory (${data?.status}). Please try again.`;
        toast.error("Create Failed", msg);
        return;
      }

      const record = data?.data ?? data;
      toast.success("Subcategory Added!", `"${record?.name || body.name}" has been added successfully.`);
      formDraft.clear();
      setShowAdd(false);
      fetchData();
    } catch (err) {
      toast.dismiss(tid);
      toast.error(
        "Network Error",
        err?.name === "TypeError"
          ? "Unable to reach the server. Check your connection."
          : "An unexpected error occurred while creating the subcategory."
      );
    } finally {
      setActionLoading(false);
    }
  };

  const handleUpdate = async (body, setErrors) => {
    setActionLoading(true);
    const tid = toast.loading("Updating subcategory…");
    try {
      const { data } = await api.update(editItem.id, body);
      toast.dismiss(tid);

      if (data?.status >= 400) {
        if (data?.data && applyFieldErrors(data?.data, setErrors)) {
          toast.error("Validation Error", "Please fix the highlighted fields.");
          return;
        }
        const msg = data?.data?.detail || `Failed to update subcategory (${data?.status}). Please try again.`;
        toast.error("Update Failed", msg);
        return;
      }

      const record = data?.data?.data ?? data?.data ?? data;
      toast.success("Subcategory Updated!", `"${record?.name || editItem.name}" has been updated successfully.`);
      setEditItem(null);
      fetchData();
    } catch (err) {
      toast.dismiss(tid);
      toast.error(
        "Network Error",
        err?.name === "TypeError"
          ? "Unable to reach the server. Check your connection."
          : "An unexpected error occurred while updating the subcategory."
      );
    } finally {
      setActionLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!delItem) return;
    setActionLoading(true);
    setDeletingId(delItem.id);
    const tid = toast.loading(`Deactivating "${delItem.name}"…`);
    try {
      const { data } = await api.remove(delItem.id);
      toast.dismiss(tid);

      if (data?.status >= 400) {
        const msg = data?.data?.detail || data?.data?.message || `Deactivation failed (${data?.status}). Please try again.`;
        toast.error("Deactivate Failed", msg);
        return;
      }

      toast.success("Deactivated!", data?.data?.message || `"${delItem.name}" has been deactivated successfully.`);
      setDelItem(null);
      fetchData();
    } catch (err) {
      toast.dismiss(tid);
      toast.error("Network Error", "Could not complete deactivation. Please try again.");
    } finally {
      setActionLoading(false);
      setDeletingId(null);
    }
  };

  // ── CHANGE 2: handleActivateConfirm reads from activateItem state ──────────
  const handleActivateConfirm = async () => {
    if (!activateItem) return;
    setActivateLoading(true);
    setActivatingId(activateItem.id);
    const tid = toast.loading(`Activating "${activateItem.name}"…`);
    try {
      const { data } = await api.update(activateItem.id, { is_active: true });
      toast.dismiss(tid);

      if (data?.status >= 400) {
        const msg = data?.data?.detail || `Activation failed (${data?.status}). Please try again.`;
        toast.error("Activate Failed", msg);
        return;
      }

      toast.success("Activated!", `"${activateItem.name}" is now active.`);
      setActivateItem(null);
      fetchData();
    } catch (err) {
      toast.dismiss(tid);
      toast.error("Network Error", "Could not activate. Please try again.");
    } finally {
      setActivateLoading(false);
      setActivatingId(null);
    }
  };

  const openEdit = async (item) => {
    try {
      const { data } = await api.get(item.id);
      const src  = data?.id ? data : item;
      setEditItem({
        id:            src.id,
        category:      src.category    ?? src.category_id ?? item.category_id ?? "",
        category_id:   src.category    ?? src.category_id ?? item.category_id ?? "",
        category_name: src.category_name ?? item.category_name ?? "",
        name:          src.name          ?? "",
        description:   src.description  ?? "",
        sort_order:    src.sort_order    ?? "",
        is_active:     src.is_active     ?? true,
      });
    } catch {
      setEditItem(item);
    }
  };

  const hasFilter = search || categoryFilter || activeFilter;

  const inputBase = {
    padding: "8px 12px", border: "1px solid #e2e8f0", borderRadius: "8px",
    fontSize: "14px", background: "#fff", color: "#1e293b", outline: "none", fontFamily: "inherit",
  };

  const COLS = ["Sr No.", "Subcategory", "Category", "Description", "Status", "Created At", "Actions"];

  return (
    <div style={{ fontFamily: "'Inter', -apple-system, sans-serif", padding: "24px", background: "#f8fafc", minHeight: "100vh", color: "#1e293b" }}>
      <style>{`
        @keyframes smShimmer   { 0%   { background-position:  200% 0; }  100% { background-position: -200% 0; } }
        @keyframes smFadeUp    { from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: translateY(0); } }
        @keyframes smSpin      { to   { transform: rotate(360deg); } }
        @keyframes smToastIn   { from { opacity: 0; transform: translateX(24px) scale(0.97); } to { opacity: 1; transform: translateX(0) scale(1); } }
        @keyframes smToastOut  { from { opacity: 1; transform: translateX(0) scale(1); }  to { opacity: 0; transform: translateX(28px) scale(0.95); } }
        .sm-row:hover          { background: #f8fafc !important; }
        .sm-edit-btn:hover     { background: #dbeafe !important; }
        .sm-del-btn:hover      { background: #ffe4e6 !important; }
        .sm-act-btn:hover      { background: #bbf7d0 !important; }
        .sm-add-btn:hover      { background: #2563eb !important; }
        .sm-page-btn:hover     { background: #f1f5f9 !important; border-color: #cbd5e1 !important; }
        * { box-sizing: border-box; }
      `}</style>

      <ToastContainer />

      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "20px", flexWrap: "wrap", gap: "12px" }}>
        <div>
          <h1 style={{ margin: 0, fontSize: "22px", fontWeight: 700, color: "#0f172a" }}>Sub Category Master</h1>
          <p style={{ margin: "4px 0 0", fontSize: "13px", color: "#64748b" }}>Manage subcategories across all categories</p>
        </div>
        <button className="sm-add-btn" onClick={() => setShowAdd(true)}
          style={{ display: "flex", alignItems: "center", gap: "6px", padding: "9px 18px", background: "#3b82f6", color: "#fff", border: "none", borderRadius: "8px", fontSize: "14px", fontWeight: 600, cursor: "pointer", transition: "background 0.15s" }}>
          <PlusIcon /> Add Subcategory
        </button>
      </div>

      {/* ── CHANGE 1: Stats bar above the search/filter row ── */}
      <StatsBar allData={allData} loading={loading} />

      <div style={{ background: "#fff", borderRadius: 12, padding: "14px 18px", border: "1px solid #e2e8f0", marginBottom: 16, display: "flex", gap: 12, flexWrap: "wrap", alignItems: "center", boxShadow: "0 1px 3px rgba(0,0,0,0.04)" }}>
        <div style={{ position: "relative", flex: "1 1 200px" }}>
          <span style={{ position: "absolute", left: "10px", top: "50%", transform: "translateY(-50%)", color: "#94a3b8", display: "flex", pointerEvents: "none" }}>
            <SearchIcon />
          </span>
          <input
            style={{ ...inputBase, width: "100%", paddingLeft: "34px", boxSizing: "border-box" }}
            placeholder="Search subcategories…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onFocus={(e) => e.target.style.borderColor = "#3b82f6"}
            onBlur={(e)  => e.target.style.borderColor = "#e2e8f0"}
          />
        </div>

        <CategoryFilterDropdown value={categoryFilter} onChange={setCategoryFilter} />

        <select style={{ ...inputBase, cursor: "pointer" }} value={activeFilter} onChange={(e) => setActiveFilter(e.target.value)}>
          <option value="">All Status</option>
          <option value="true">Active</option>
          <option value="false">Inactive</option>
        </select>

        {hasFilter && (
          <button
            onClick={() => { setSearch(""); setCategoryFilter(null); setActiveFilter(""); }}
            style={{ ...inputBase, display: "flex", alignItems: "center", gap: "5px", cursor: "pointer", background: "#fff5f5", color: "#dc2626", border: "1px solid #fecaca", fontWeight: 600, fontSize: 13, whiteSpace: "nowrap" }}
          >
            <CloseIcon /> Clear
          </button>
        )}
      </div>

      <div style={{ background: "#fff", borderRadius: "12px", border: "1px solid #e2e8f0", overflow: "hidden", boxShadow: "0 1px 4px rgba(0,0,0,0.05)" }}>
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "14px" }}>
            <thead>
              <tr>
                {COLS.map((h) => (
                  <th key={h} style={{ padding: "11px 16px", textAlign: "left", fontSize: "12px", fontWeight: 600, color: "#64748b", textTransform: "uppercase", letterSpacing: "0.05em", background: "#f8fafc", borderBottom: "1px solid #e2e8f0", whiteSpace: "nowrap" }}>
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <TableSkeleton />
              ) : allData.length === 0 ? (
                <tr>
                  <td colSpan={COLS.length}>
                    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", padding: "56px 20px", gap: "8px" }}>
                      <div style={{ width: "52px", height: "52px", background: "#f1f5f9", borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "22px", marginBottom: "4px" }}>🗂️</div>
                      <p style={{ margin: 0, fontSize: "16px", fontWeight: 600, color: "#374151" }}>No subcategories found</p>
                      <p style={{ margin: 0, fontSize: "13px", color: "#94a3b8" }}>
                        {hasFilter ? "Try adjusting your filters" : "Add your first subcategory to get started"}
                      </p>
                      {!hasFilter && (
                        <button onClick={() => setShowAdd(true)}
                          style={{ display: "flex", alignItems: "center", gap: "6px", marginTop: "8px", padding: "8px 18px", background: "#3b82f6", color: "#fff", border: "none", borderRadius: "8px", fontSize: "14px", fontWeight: 600, cursor: "pointer" }}>
                          <PlusIcon /> Add Subcategory
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ) : (
                pagedData.map((item, idx) => {
                  const rowNum  = (currentPage - 1) * PAGE_SIZE + idx + 1;
                  const isBusy  = deletingId === item.id || activatingId === item.id;
                  return (
                    <tr key={item.id} className="sm-row"
                      style={{ borderBottom: "1px solid #f1f5f9", opacity: isBusy ? 0.35 : 1, pointerEvents: isBusy ? "none" : "auto", transition: "opacity 0.2s" }}>
                      <td style={{ padding: "13px 16px", color: "#94a3b8", fontSize: "13px" }}>{rowNum}</td>
                      <td style={{ padding: "13px 16px" }}>
                        <div style={{ fontWeight: 600, color: "#0f172a", fontSize: "14px" }}>{item.name}</div>
                      </td>
                      <td style={{ padding: "13px 16px" }}>
                        <span style={{ display: "inline-flex", alignItems: "center", background: "#eef2ff", color: "#4f46e5", fontSize: "12px", fontWeight: 600, borderRadius: "6px", padding: "3px 10px" }}>
                          {item.category_name || "—"}
                        </span>
                      </td>
                      <td style={{ padding: "13px 16px", maxWidth: "180px" }}>
                        <div style={{ fontSize: "13px", color: item.description ? "#64748b" : "#cbd5e1", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                          {item.description || "—"}
                        </div>
                      </td>
                      <td style={{ padding: "13px 16px" }}><Badge active={item.is_active} /></td>
                      <td style={{ padding: "13px 16px", color: "#94a3b8", fontSize: "12px", whiteSpace: "nowrap" }}>{item.created_at || "—"}</td>
                      <td style={{ padding: "13px 16px" }}>
                        <div style={{ display: "flex", gap: "6px" }}>
                          <button className="sm-edit-btn" title="Edit" onClick={() => openEdit(item)}
                            style={{ display: "flex", alignItems: "center", padding: "6px 10px", gap: "5px", background: "#eff6ff", border: "1px solid #bfdbfe", borderRadius: "6px", cursor: "pointer", color: "#3b82f6", fontSize: "12px", fontWeight: 600, transition: "background 0.15s" }}>
                            <EditIcon /> Edit
                          </button>
                          {item.is_active ? (
                            <button className="sm-del-btn" title="Deactivate" onClick={() => setDelItem(item)}
                              style={{ display: "flex", alignItems: "center", padding: "6px 10px", gap: "5px", background: "#fff1f2", border: "1px solid #fecdd3", borderRadius: "6px", cursor: "pointer", color: "#f43f5e", fontSize: "12px", fontWeight: 600, transition: "background 0.15s" }}>
                              Deactivate
                            </button>
                          ) : (
                            // ── CHANGE 2: open confirm dialog instead of firing directly ──
                            <button className="sm-act-btn" title="Activate" onClick={() => setActivateItem(item)}
                              style={{ display: "flex", alignItems: "center", padding: "6px 10px", gap: "5px", background: "#f0fdf4", border: "1px solid #bbf7d0", borderRadius: "6px", cursor: "pointer", color: "#16a34a", fontSize: "12px", fontWeight: 600, transition: "background 0.15s" }}>
                              Activate
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {!loading && allData.length > 0 && (
          <Pagination currentPage={currentPage} totalPages={totalPages} totalCount={totalCount} onPageChange={handlePageChange} />
        )}
      </div>

      {showAdd && (
        <Modal title="Add New Subcategory" onClose={() => { formDraft.clear(); setShowAdd(false); }}>
          <SubcategoryForm onSubmit={handleCreate} onCancel={() => { formDraft.clear(); setShowAdd(false); }} loading={actionLoading} />
        </Modal>
      )}

      {editItem && (
        <Modal title={`Edit Subcategory — ${editItem.name}`} onClose={() => setEditItem(null)}>
          <SubcategoryForm initial={editItem} onSubmit={handleUpdate} onCancel={() => setEditItem(null)} loading={actionLoading} />
        </Modal>
      )}

      {/* Deactivate confirm (unchanged) */}
      {delItem && (
        <Modal title="Deactivate Subcategory" onClose={() => setDelItem(null)}>
          <DeactivateConfirm item={delItem} onConfirm={handleDelete} onCancel={() => setDelItem(null)} loading={actionLoading} />
        </Modal>
      )}

      {/* ── CHANGE 2: Activate confirm dialog ── */}
      {activateItem && (
        <Modal title="Activate Subcategory" onClose={() => setActivateItem(null)}>
          <ActivateConfirm item={activateItem} onConfirm={handleActivateConfirm} onCancel={() => setActivateItem(null)} loading={activateLoading} />
        </Modal>
      )}
    </div>
  );
};

export default SubcategoryMaster;